/**
 * Conflict Detection Query Functions
 * 
 * Checks for conflicts between absence notes and attendance records.
 * @module fehlzeiten/services/queries/conflictQueries
 */

import { supabase } from '../../../../../lib/supabase';

export interface AbsenceConflict {
    conflictDate: string;
    presenceStatus: string;
    checkInTime: string | null;
    checkOutTime: string | null;
    hasLessons: boolean;
    lessonCount: number;
}

/**
 * Checks for conflicts between absence note and actual attendance.
 */
export async function checkAbsenceConflicts(params: {
    studentId: string;
    startDate: string;
    endDate?: string | null;
}): Promise<AbsenceConflict[]> {
    const { studentId, startDate, endDate } = params;

    const { data, error } = await supabase.rpc('fn_check_absence_conflicts' as never, {
        p_student_id: studentId,
        p_start_date: startDate,
        p_end_date: endDate || startDate,
    } as never);

    if (error) {
        console.error('[checkAbsenceConflicts] Error:', error.message);
        return [];
    }

    if (!Array.isArray(data)) {
        return [];
    }

    return data.map((row: Record<string, unknown>) => ({
        conflictDate: row.conflict_date as string,
        presenceStatus: row.presence_status as string,
        checkInTime: row.check_in_time as string | null,
        checkOutTime: row.check_out_time as string | null,
        hasLessons: row.has_lessons as boolean,
        lessonCount: row.lesson_count as number,
    }));
}
